import type { StringJoin, IsNever, IsUnion, SwitchValue } from '../index.js';
import { as } from './as.js';

/**
 * Gets the name of a a single property from an object, as a string literal
 * type. It only accepts single-property objects, and will fail if you try to
 * pass an object with multiple properties.
 *
 * Why is this useful? Because it survives minification, and is a type safe
 * alternative to using strings directly. It's especially useful for setting
 * the `displayName` of a React component, for example:
 * ```ts
 * declare const MyComponent: React.FC;
 * // Set displayName to "MyComponent":
 * MyComponent.displayName = nameof({ MyComponent });
 * ```
 *
 * For namespace support, you can provide multiple single-property objects as
 * arguments, for example:
 * ```ts
 * namespace A.B.C {
 *   export declare const MyComponent: React.FC;
 *   // Set displayName to "A.B.C.MyComponent":
 *   MyComponent.displayName = nameof({ A }, { B }, { C }, { MyComponent });
 * }
 * ```
 *
 * Or you can use the `prefix` function to build up a name in stages:
 * ```ts
 * // Scoped for namespace A:
 * import { nameof as _nameof } from '@smd/utilities';
 * export const nameof = _nameof.prefix({ A });
 *
 * // Scoped for namespace A + B + C:
 * import { nameof as _nameof } from '../nameof';
 * export const nameof = _nameof.prefix({ B }, { C });
 *
 * // Somewhere inside C:
 * import { nameof } from './nameof';
 * // Set displayName to "A.B.C.MyComponent":
 * MyComponent.displayName = nameof({ MyComponent });
 * ```
 */
export const nameof = (function construct<
  TPrevObjects extends nameof.StringPropertyObject.ZeroOrMore
>(...prev: TPrevObjects): nameof<TPrevObjects> {
  const next: nameof<TPrevObjects> = (...singlePropertyObjects) =>
    as(
      [...prev, ...singlePropertyObjects]
        .map(Object.keys)
        .map(([singlePropertyName]) => singlePropertyName)
        .join('.')
    );

  next.prefix = (...singlePropertyObjects) =>
    as(construct(...prev, ...singlePropertyObjects));

  return next;
})();

export declare type nameof<
  T extends nameof.StringPropertyObject.ZeroOrMore = []
> = {
  <TObjects extends nameof.StringPropertyObject.OneOrMore>(
    ...singlePropertyObjects: nameof.SingleProperty.Objects.From<TObjects>
  ): nameof.Result<
    [...T, ...TObjects] extends nameof.StringPropertyObject.OneOrMore
      ? [...T, ...TObjects]
      : never
  >;

  prefix<TObjects extends nameof.StringPropertyObject.OneOrMore>(
    ...singlePropertyObjects: nameof.SingleProperty.Objects.From<TObjects>
  ): nameof<
    [...T, ...TObjects] extends nameof.StringPropertyObject.OneOrMore
      ? [...T, ...TObjects]
      : never
  >;
};

export declare namespace nameof {
  export type Namespace = { nameof: nameof<never> };

  export type Result<TObjects extends StringPropertyObject.OneOrMore> =
    StringJoin<SingleProperty.Names.From<TObjects>, '.'>;
}

export declare namespace nameof {
  export type StringPropertyObject = Record<string, unknown>;

  export namespace StringPropertyObject {
    export type ZeroOrMore = ReadonlyArray<StringPropertyObject>;

    export type OneOrMore = [
      StringPropertyObject,
      ...Array<StringPropertyObject>
    ];
  }
}

export declare namespace nameof.SingleProperty {
  export type Validate<TObject extends StringPropertyObject> =
    IsNever<keyof TObject> extends true
      ? false
      : IsUnion<keyof TObject> extends false
        ? true
        : false;

  export namespace Names {
    export type From<TObjects extends StringPropertyObject.OneOrMore> = {
      [TIndex in keyof TObjects]: SwitchValue<
        Validate<TObjects[TIndex]>,
        keyof TObjects[TIndex]
      >;
    };
  }

  export namespace Objects {
    export type From<TObjects extends StringPropertyObject.OneOrMore> = {
      [TIndex in keyof TObjects]: SwitchValue<
        Validate<TObjects[TIndex]>,
        TObjects[TIndex]
      >;
    };
  }
}
